import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { firebaseApp } from "../../firebase";
import {
  collection,
  getFirestore,
  onSnapshot,
  query,
  orderBy,
  where,
  doc,
  getDoc,
  getDocs,
} from "firebase/firestore";
import {
  Briefcase,
  Clock,
  CheckCircle,
  Eye,
  ShieldX,
  Star,
  TrendingUp,
  AlertCircle,
  Filter,
  X,
  Search,
  Sparkles,
  Target,
  Zap,
  Building2,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useVerificationStatus } from "../../hooks/useVerificationStatus";
import { useSubscription } from "../../hooks/useSubscription";
import VerificationRequiredModal from "../../components/VerificationRequiredModal";
import { filterTestsBySkillMatch } from "../utils/skillMatching";
import Tooltip from "../components/JobSeekerTooltip";
import PremiumPaywall from "../../components/PremiumPaywall";

const AvailableTests = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isVerified } = useVerificationStatus();
  const { isPro } = useSubscription();
  const [tests, setTests] = useState([]);
  const [completedTestIds, setCompletedTestIds] = useState(
    new Set()
  );
  const [loading, setLoading] = useState(true);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [userSkills, setUserSkills] = useState([]);
  const [filteredTests, setFilteredTests] = useState([]);
  const [showAllTests, setShowAllTests] = useState(false);
  const [hrCompanies, setHrCompanies] = useState(new Map());

  // New filter states
  const [recommendationsEnabled, setRecommendationsEnabled] = useState(true);
  const [userClassification, setUserClassification] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagSearchTerm, setTagSearchTerm] = useState("");
  const [allAvailableTags, setAllAvailableTags] = useState([]);
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  // job description modal
  const [showJDModal, setShowJDModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  // Fetch user skills and classification from profile
  useEffect(() => {
    if (!user) return;

    const fetchUserProfile = async () => {
      try {
        const db = getFirestore(firebaseApp);

        // Fetch from userProfiles for skills and classification
        const profileDoc = await getDoc(doc(db, "userProfiles", user.id));

        // Also fetch from jobseekers for classification (in case it's only there)
        const jobseekerDoc = await getDoc(doc(db, "jobseekers", user.id));

        if (profileDoc.exists()) {
          const profileData = profileDoc.data();
          const resumeSkills =
            profileData.resumeData?.professionalInfo?.skills ||
            profileData.resumeData?.skills ||
            [];

          // Convert to the format expected by skill matching
          const skills = resumeSkills.map((skill) => ({
            name: skill,
            verified: profileData.skillsVerified || false,
            score: profileData.verificationScore,
          }));

          setUserSkills(skills);

          // Set user classification
          const classification = {
            isTechnical: profileData.isTechnical,
            profileType: profileData.profileType,
            primaryDomain: profileData.primaryDomain,
          };
          setUserClassification(classification);
        }

        // Fallback to jobseekers collection if classification not in userProfiles
        if (jobseekerDoc.exists() && !userClassification) {
          const jobseekerData = jobseekerDoc.data();
          if (jobseekerData.isTechnical !== undefined) {
            setUserClassification({
              isTechnical: jobseekerData.isTechnical,
              profileType: jobseekerData.profileType,
              primaryDomain: jobseekerData.primaryDomain,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      }
    };

    fetchUserProfile();
  }, [user, isVerified]);

  // Fetch

  useEffect(() => {
    const db = getFirestore(firebaseApp);
    const q = query(collection(db, "hrTests"), orderBy("createdAt", "desc"));

    const unsub = onSnapshot(q, async (snap) => {
      // Filter out disabled tests - only show tests where isEnabled is true (or undefined for backwards compatibility)
      const docs = snap.docs
        .map((d) => ({ id: d.id, ...(d.data()) }))
        .filter((test) => test.isEnabled !== false);

      // Fetch HR company names for all tests
      const hrIds = [...new Set(docs.map((test) => test.hrId).filter(Boolean))];
      const companiesMap = new Map();

      // Fetch all HR Jobs to link descriptions and titles
      const hrJobMap = new Map();
      const jdDocSnap = await getDocs(collection(db, "hrJobs"));
      jdDocSnap.forEach((docSnap) => {
        hrJobMap.set(docSnap.id, docSnap.data());
      });

      for (const hrId of hrIds) {
        try {
          // Fix: HR Profiles are stored in 'hrProfiles', not 'recruiters'
          const hrDoc = await getDoc(doc(db, "hrProfiles", hrId));
          if (hrDoc.exists()) {
            const hrData = hrDoc.data();
            companiesMap.set(hrId, hrData.company || "Company Not Specified");
          } else {
            companiesMap.set(hrId, "VetriaAI Partner"); // Fallback if no profile
          }
        } catch (error) {
          console.error(`Error fetching HR data for ${hrId}:`, error);
        }
      }

      // Extract all unique tags from tests
      const tagsSet = new Set();
      docs.forEach((test) => {
        // Link to Job Data using test.jobId
        const linkedJob = test.jobId ? hrJobMap.get(test.jobId) : null;

        // Always assign company (from Recruiter)
        test.company = companiesMap.get(test.hrId) || "Company Not Specified";

        // Assign Job Description (from Linked Job)
        test.jobDescription = linkedJob?.jobDescription || "Job Description Not Specified";

        // Fallback for Job Title if missing on Test
        if (!test.jobTitle || test.jobTitle === "Untitled Test") {
          // If linkedJob exists, use its title
          if (linkedJob?.jobTitle) {
            test.jobTitle = linkedJob.jobTitle;
          } else if (test.jobId && test.jobId.startsWith("preset_")) {
            const presetTitle = test.jobId.replace("preset_", "").replace(/^\w/, c => c.toUpperCase()) + " Engineer";
            test.jobTitle = presetTitle;
          } else {
            test.jobTitle = "Untitled Position";
          }
        }

        if (test.tags && Array.isArray(test.tags)) {
          test.tags.forEach((tag) => tagsSet.add(tag));
        }
      });
      setAllAvailableTags(Array.from(tagsSet).sort());

      setHrCompanies(companiesMap);
      setTests(docs);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Comprehensive filtering logic
  useEffect(() => {
    if (tests.length === 0) {
      setFilteredTests([]);
      return;
    }

    setIsFilterLoading(true);

    // Apply filters
    let filtered = [...tests];

    // 1. Sort by technical/non-technical match (if recommendations are ON)
    // Note: We no longer FILTER out tests - we just prioritize matching ones
    // This ensures all tests are visible, but better matches appear first
    if (
      recommendationsEnabled &&
      userClassification &&
      userClassification.isTechnical !== undefined
    ) {
      filtered = filtered.sort((a, b) => {
        // Tests matching user's technical classification come first
        const aMatches = a.isTechnical === userClassification.isTechnical;
        const bMatches = b.isTechnical === userClassification.isTechnical;

        if (aMatches && !bMatches) return -1;
        if (!aMatches && bMatches) return 1;
        return 0;
      });
    }

    // 2. Filter by selected tags (AND logic - test must have ALL selected tags)
    if (selectedTags.length > 0) {
      filtered = filtered.filter((test) => {
        if (!test.tags || test.tags.length === 0) return false;

        // Test must have ALL selected tags (case-insensitive)
        return selectedTags.every((selectedTag) =>
          test.tags?.some(
            (testTag) => testTag.toLowerCase() === selectedTag.toLowerCase()
          )
        );
      });
    }

    // 3. Apply skill matching (existing logic)
    if (userSkills.length > 0 && !showAllTests) {
      filtered = filterTestsBySkillMatch(userSkills, filtered);
    }

    setFilteredTests(filtered);
    setIsFilterLoading(false);
  }, [
    tests,
    userSkills,
    recommendationsEnabled,
    userClassification,
    selectedTags,
    showAllTests,
  ]);

  // Fetch user's completed tests
  useEffect(() => {
    if (!user) return;

    const db = getFirestore(firebaseApp);
    const q = query(
      collection(db, "jobseekerResults"),
      where("jobSeekerId", "==", user.id)
    );

    const unsub = onSnapshot(q, (snap) => {
      const completedIds = new Set();
      snap.docs.forEach((doc) => {
        const data = doc.data();
        if (data.testId) {
          completedIds.add(data.testId);
        }
      });
      setCompletedTestIds(completedIds);
    });

    return () => unsub();
  }, [user]);

  const handleStartTest = (testId) => {
    if (!isVerified) {
      setShowVerificationModal(true);
      return;
    }
    // If verified, navigate to test
    navigate(`/job-seeker/take-test/${testId}`);
  };

  const openJD = (job) => {
    if (!job) return;
    setSelectedJob(job);
    setShowJDModal(true);
  };

  const closeJD = () => {
    setShowJDModal(false);
    setSelectedJob(null);
  };

  if (loading) return <div className="text-center py-10">Loading tests...</div>;

  // Show verification required message if user is not verified
  if (!isVerified) {
    return (
      <div className="max-w-3xl mx-auto py-12 px-6">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 text-center">
          {/* Icon */}
          <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <ShieldX className="w-10 h-10 text-yellow-600" />
          </div>

          {/* Title */}
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Verify Your Skills to Unlock This Feature
          </h1>

          {/* Message */}
          <p className="text-lg text-gray-600 mb-6 leading-relaxed">
            To ensure quality and maintain standards, only verified candidates
            can access and attend tests. Please complete your skills
            verification to unlock all available tests.
          </p>

          {/* Benefits */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8 text-left max-w-md mx-auto">
            <h3 className="font-semibold text-blue-900 mb-4 flex items-center">
              <Star className="w-5 h-5 mr-2" />
              Why Verify Your Skills?
            </h3>
            <ul className="text-blue-800 space-y-3">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>Access all available job tests and opportunities</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>Get a verified badge on your profile</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>Stand out to employers with verified credentials</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>Quick AI-generated assessment based on your resume</span>
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/job-seeker/skills-validation"
              className="inline-flex items-center justify-center px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
            >
              <Star className="w-5 h-5 mr-2" />
              Verify Skills Now
            </Link>

            <Link
              to="/job-seeker/profile"
              className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              View Profile
            </Link>
          </div>

          {/* Additional Note */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Need 60% or higher score to get verified • Quick 20-question
              assessment
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Helper functions for tag management
  const addTag = (tag) => {
    if (!selectedTags.includes(tag)) {
      setSelectedTags([...selectedTags, tag]);
    }
    setTagSearchTerm("");
  };

  const removeTag = (tag) => {
    setSelectedTags(selectedTags.filter((t) => t !== tag));
  };

  const filteredTagSuggestions = allAvailableTags
    .filter(
      (tag) =>
        tag.toLowerCase().includes(tagSearchTerm.toLowerCase()) &&
        !selectedTags.includes(tag)
    )
    .slice(0, 10);

  const displayTests = showAllTests ? tests : filteredTests;

  return (
    <PremiumPaywall isPremium={isPro}>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header with Filters Toggle */}
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900">Available Tests</h1>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Filter className="w-4 h-4 mr-2" />
            {showFilters ? "Hide Filters" : "Show Filters"}
          </button>
        </div>

        {/* Filter Bar */}
        {showFilters && (
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-6">
            {/* Recommendation Toggle */}
            <div className="flex items-center justify-between pb-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <Sparkles className="w-5 h-5 text-purple-600" />
                <div>
                  <h3 className="font-semibold text-gray-900">
                    AI Recommendations
                  </h3>
                  <p className="text-sm text-gray-600">
                    {recommendationsEnabled ? (
                      userClassification?.isTechnical !== undefined ? (
                        <>
                          Showing{" "}
                          {userClassification.isTechnical
                            ? "Technical"
                            : "Non-Technical"}{" "}
                          tests for you
                        </>
                      ) : (
                        <>Upload resume to enable smart recommendations</>
                      )
                    ) : (
                      <>Showing all tests regardless of match</>
                    )}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setRecommendationsEnabled(!recommendationsEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${recommendationsEnabled ? "bg-purple-600" : "bg-gray-300"
                  }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${recommendationsEnabled ? "translate-x-6" : "translate-x-1"
                    }`}
                />
              </button>
            </div>

            {/* Tag-based Search */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="font-semibold text-gray-900 flex items-center">
                  <Search className="w-4 h-4 mr-2 text-gray-600" />
                  Search by Tags
                </label>
                {selectedTags.length > 0 && (
                  <button
                    onClick={() => setSelectedTags([])}
                    className="text-xs text-red-600 hover:text-red-700 underline"
                  >
                    Clear all tags
                  </button>
                )}
              </div>

              {/* Selected Tags */}
              {selectedTags.length > 0 && (
                <div className="flex flex-wrap gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  {selectedTags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-blue-600 text-white text-sm rounded-full flex items-center space-x-1"
                    >
                      <span>{tag}</span>
                      <button
                        onClick={() => removeTag(tag)}
                        className="ml-1 hover:text-blue-200"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Tag Search Input */}
              <div className="relative">
                <input
                  type="text"
                  value={tagSearchTerm}
                  onChange={(e) => setTagSearchTerm(e.target.value)}
                  placeholder="Search tags (e.g., React, Python, Marketing...)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />

                {/* Tag Suggestions Dropdown */}
                {tagSearchTerm && filteredTagSuggestions.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredTagSuggestions.map((tag) => (
                      <button
                        key={tag}
                        onClick={() => addTag(tag)}
                        className="w-full px-4 py-2 text-left hover:bg-blue-50 transition-colors text-sm"
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <p className="text-xs text-gray-500">
                {allAvailableTags.length} tags available • Select multiple tags to
                narrow results
              </p>
            </div>

            {/* Filter Summary */}
            <div className="pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-700">
                  Showing {isFilterLoading ? "..." : displayTests.length} of{" "}
                  {tests.length} tests
                </span>
                {(selectedTags.length > 0 || !recommendationsEnabled) && (
                  <button
                    onClick={() => {
                      setSelectedTags([]);
                      setRecommendationsEnabled(true);
                    }}
                    className="text-red-600 hover:text-red-700 font-medium"
                  >
                    Reset Filters
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Take Practice Test Card */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Target className="w-7 h-7 text-green-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-900 mb-1">
                Take Practice Test
              </h2>
              <p className="text-gray-600">
                Improve your skills and get ready for real assessments
              </p>
            </div>
            <Link
              to="/job-seeker/take-test/demo"
              className="px-6 py-3 bg-red-800 text-white rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center space-x-2"
            >
              <Zap className="w-4 h-4" />
              <span>Start Practice</span>
            </Link>
          </div>
        </div>

        {/* Skills-based filtering info */}
        {userSkills.length > 0 && !showAllTests && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Star className="w-4 h-4 text-blue-600" />
              <span className="text-blue-900 font-medium">
                Personalized Recommendations
              </span>
            </div>
            <p className="text-blue-800 text-sm">
              Tests are ranked based on your skills and profile.
              {` Showing ${filteredTests.length} available tests.`}
            </p>
          </div>
        )}

        {/* No skills warning */}
        {userSkills.length === 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <AlertCircle className="w-4 h-4 text-yellow-600" />
              <span className="text-yellow-900 font-medium">
                Upload Resume for Better Recommendations
              </span>
            </div>
            <p className="text-yellow-800 text-sm">
              Upload your resume to get personalized test recommendations based on
              your skills.
            </p>
          </div>
        )}

        {/* Loading Animation */}
        {isFilterLoading && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-center space-x-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="text-blue-900">Applying filters...</span>
            </div>
          </div>
        )}

        {displayTests.length === 0 && !isFilterLoading && (
          <div className="text-center py-20 bg-white border rounded-lg">
            <p>
              {tests.length === 0
                ? "No tests have been published yet. Please check back later."
                : selectedTags.length > 0
                  ? "No tests match the selected tags. Try removing some filters."
                  : "No tests match your current criteria. Try adjusting your filters or showing all tests."}
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayTests.map((t) => {
            const isCompleted = completedTestIds.has(t.id);

            return (
              <div
                key={t.id}
                className="group bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col h-full relative overflow-hidden"
              >
                {/* Decorative top gradient line */}
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-500 to-red-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>

                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3 w-full">
                    <div className="w-12 h-12 rounded-xl bg-red-50 flex-shrink-0 flex items-center justify-center text-red-600 border border-red-100">
                      <Building2 className="w-6 h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3
                        className="font-bold text-gray-900 text-base line-clamp-1"
                        title={t.jobTitle || "Untitled Position"}
                      >
                        {t.jobTitle || "Untitled Position"}
                      </h3>
                      <p className="text-sm text-gray-500 font-medium truncate">
                        {t.company || "Company Confidential"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Stats Row */}
                <div className="flex items-center gap-3 mb-4 text-xs font-medium text-gray-500">
                  {t.isTechnical !== undefined && (
                    <span className={`px-2 py-0.5 rounded-full ${t.isTechnical ? 'bg-purple-50 text-purple-700 border border-purple-100' : 'bg-teal-50 text-teal-700 border border-teal-100'}`}>
                      {t.isTechnical ? "Technical" : "Non-Tech"}
                    </span>
                  )}
                  {typeof t.duration === "number" && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {t.duration}m
                    </span>
                  )}
                  {t.questionCount && <span>{t.questionCount} Qs</span>}
                </div>

                {/* Match Score Bar (if applicable) */}
                {t.matchScore && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="font-semibold text-gray-700">Match Score</span>
                      <span className={`font-bold ${t.matchScore >= 70 ? 'text-green-600' : t.matchScore >= 50 ? 'text-yellow-600' : 'text-red-500'}`}>
                        {t.matchScore}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                      <div
                        className={`h-full rounded-full ${t.matchScore >= 70 ? 'bg-green-500' : t.matchScore >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                        style={{ width: `${t.matchScore}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {/* Tags Body */}
                <div className="flex-1 mb-5">
                  {t.tags && t.tags.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {t.tags.slice(0, 3).map((tag, i) => (
                        <span key={i} className="px-2 py-1 bg-gray-50 text-gray-600 text-[10px] uppercase tracking-wide font-semibold rounded-md border border-gray-100">
                          {tag}
                        </span>
                      ))}
                      {t.tags.length > 3 && <span className="text-[10px] text-gray-400 self-center">+{t.tags.length - 3}</span>}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 italic">No tags specified</p>
                  )}
                </div>

                {/* Footer Actions */}
                <div className="mt-auto grid grid-cols-2 gap-3 pt-4 border-t border-gray-50">
                  <button
                    type="button"
                    onClick={() => openJD(t)}
                    className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 rounded-xl transition-all"
                  >
                    View Details
                  </button>
                  {isCompleted ? (
                    <Link
                      to="/job-seeker/results"
                      className="px-4 py-2 text-sm font-semibold text-green-700 bg-green-50 border border-green-100 hover:bg-green-100 rounded-xl transition-all text-center flex items-center justify-center gap-2"
                    >
                      <CheckCircle className="w-4 h-4" /> Results
                    </Link>
                  ) : (
                    <button
                      onClick={() => handleStartTest(t.id)}
                      className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 shadow-lg shadow-red-100 hover:shadow-red-200 rounded-xl transition-all flex items-center justify-center gap-2 group-hover:scale-[1.02]"
                    >
                      Start Test <Zap className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Verification Required Modal */}
        <VerificationRequiredModal
          isOpen={showVerificationModal}
          onClose={() => setShowVerificationModal(false)}
        />
        {/* Job Description Modal */}
        {/* Job Details Modal - Redesigned */}
        {showJDModal && selectedJob && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity"
              onClick={closeJD}
              aria-hidden="true"
            />

            {/* Modal Content */}
            <div className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200">

              {/* Modal Header / Hero Section */}
              <div className="bg-gradient-to-br from-red-50 to-white px-8 py-8 border-b border-red-100 flex-shrink-0 relative">
                <button
                  onClick={closeJD}
                  className="absolute top-4 right-4 p-2 rounded-full bg-white/80 hover:bg-white text-gray-500 hover:text-gray-800 transition-colors shadow-sm"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>

                <div className="flex flex-col md:flex-row md:items-start gap-5">
                  {/* Company Logo / Icon */}
                  <div className="w-16 h-16 rounded-2xl bg-white shadow-md flex items-center justify-center text-red-600 border border-gray-100 flex-shrink-0">
                    <Building2 className="w-8 h-8" />
                  </div>

                  <div className="flex-1 space-y-2">
                    <h3 className="text-xl font-bold text-gray-900 leading-tight">
                      {selectedJob.company || "Company Confidential"}
                    </h3>
                    <h4 className="text-lg text-gray-600 font-medium">
                      {selectedJob.jobTitle || "Untitled Position"}
                    </h4>

                    {/* Quick Meta Tags */}
                    <div className="flex flex-wrap gap-2 pt-1">
                      {selectedJob.isTechnical !== undefined && (
                        <span className={`px-2.5 py-1 text-xs font-semibold rounded-md ${selectedJob.isTechnical ? 'bg-purple-100 text-purple-700' : 'bg-teal-100 text-teal-700'}`}>
                          {selectedJob.isTechnical ? "Technical" : "Non-Tech"}
                        </span>
                      )}
                      <span className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-md flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {typeof selectedJob.duration === 'number' ? selectedJob.duration : '--'} mins
                      </span>
                      <span className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-md">
                        {selectedJob.questionCount || '--'} Questions
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content Body - Scrollable */}
              <div className="p-8 overflow-y-auto flex-1 custom-scrollbar">
                <div className="prose prose-sm md:prose-base max-w-none text-gray-600">
                  <h5 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">Job Description</h5>
                  {selectedJob.jobDescription ? (
                    <p className="whitespace-pre-line leading-relaxed">
                      {selectedJob.jobDescription}
                    </p>
                  ) : (
                    <p className="italic text-gray-400">No detailed job description provided.</p>
                  )}
                </div>

                {/* Tags Section in Body */}
                {selectedJob.tags && selectedJob.tags.length > 0 && (
                  <div className="mt-8 pt-6 border-t border-gray-100">
                    <h5 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Skills & Tags</h5>
                    <div className="flex flex-wrap gap-2">
                      {selectedJob.tags.map((tag, i) => (
                        <span key={i} className="px-3 py-1 bg-gray-50 text-gray-600 text-xs font-medium rounded-full border border-gray-200">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Sticky Footer */}
              <div className="p-5 border-t border-gray-100 bg-gray-50/50 flex justify-end gap-3 flex-shrink-0">
                <button
                  onClick={closeJD}
                  className="px-5 py-2.5 bg-white border border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors shadow-sm"
                >
                  Close
                </button>

                {!completedTestIds.has(selectedJob.id) && (
                  <button
                    onClick={() => {
                      closeJD();
                      handleStartTest(selectedJob.id);
                    }}
                    className="px-6 py-2.5 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 shadow-md shadow-red-100 transition-all flex items-center gap-2"
                  >
                    <Zap className="w-4 h-4" /> Start Test Now
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </PremiumPaywall>
  );
};

export default AvailableTests;
